// ACCOUNT SUBSCRIPTION — ACTIVATE ****************************************************************
// pathname: /v1/account-subscriptions
// method:   POST
// ************************************************************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi/useSwrMutationApi';
import type { ApiSchema } from '@type/api/apiUtilityFn';

export interface ActivateSubscriptionPayload {
  planVersionId: number;
}

interface UseSwrActivateSubscriptionProps {
  locale?: string;
  onSuccess?: (subscription: ApiSchema<'AccountSubscriptionRead'>) => void;
  onError?: (error: unknown) => void;
}

/**
 * @description SWR mutation: activate a subscription for the logged account.
 *
 * Charges the first period immediately, so the failure modes are meaningful and
 * distinct — the caller is expected to tell them apart rather than show one
 * generic message:
 * - **402** the wallet declined the charge; nothing was activated
 * - **409** the account already subscribes, or the plan can no longer be sold
 * - **403** not an impersonating admin
 *
 * The error toast is suppressed so the page can render those cases inline; a
 * modal that says "something went wrong" would hide the one thing the operator
 * needs to know.
 */
export const useSwrActivateSubscription = (props?: UseSwrActivateSubscriptionProps) => {
  const { locale, onSuccess, onError } = props ?? {};

  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<ApiSchema<'AccountSubscriptionRead'>, ActivateSubscriptionPayload>({
    url: '/v1/account-subscriptions',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => { if (onSuccess) onSuccess(response); },
      onError: (err) => { if (onError) onError(err); },
    },
    toastsOptions: {
      showSuccessToast: false,
      showErrorToast: false,
    },
  });

  return {
    data,
    error,
    isMutating,
    reset,
    trigger,
  };
};
